import { type KeyboardEvent, useCallback, useMemo } from 'react'
import { type Observable } from 'rxjs'
import { ToggleGroup, ToggleItem } from '@/components/ToggleGroup'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { CustomSelect } from '@/components/inputs/CustomSelect'

export interface BoolQuestionAnswerEditorProps {
  value: boolean | null
  isDisabled: boolean
  onChange: (value: boolean) => unknown
  focus$?: Observable<unknown>
  onFocus?: () => unknown
  onEnterDown?: (e: KeyboardEvent) => unknown
  isNotValid?: boolean
  yesTestId?: string
  noTestId?: string
}

export function BoolQuestionAnswerEditor({
  value,
  isDisabled,
  onChange,
  onEnterDown,
  focus$,
  onFocus,
  isNotValid,
  yesTestId,
  noTestId,
}: BoolQuestionAnswerEditorProps) {
  const keyDown = useCallback(
    (e: KeyboardEvent) => e.key === 'Enter' && onEnterDown?.(e),
    [onEnterDown],
  )

  return (
    <div>
      <ToggleGroup stretch focus$={focus$} onFocus={onFocus}>
        <ToggleItem
          toggled={value === true}
          onChange={() => onChange(true)}
          isDisabled={isDisabled}
          onKeyDown={keyDown}
          isNotValid={isNotValid}
          testId={yesTestId}
        >
          Yes
        </ToggleItem>
        <ToggleItem
          toggled={value === false}
          onChange={() => onChange(false)}
          isDisabled={isDisabled}
          onKeyDown={keyDown}
          isNotValid={isNotValid}
          testId={noTestId}
        >
          No
        </ToggleItem>
      </ToggleGroup>
    </div>
  )
}

export interface BoolSelectQuestionAnswerEditorProps {
  value?: string
  isDisabled: boolean
  onChange: (value: boolean) => unknown
  focus$?: Observable<unknown>
  onFocus?: () => unknown
  onEnterDown?: (e: KeyboardEvent) => unknown
  testId?: string
}

export function BoolSelectQuestionAnswerEditor({
  value,
  isDisabled,
  onChange,
  onEnterDown,
  focus$,
  onFocus,
  testId,
}: BoolSelectQuestionAnswerEditorProps) {
  const itemSource = useMemo(
    () =>
      ItemSource.static<string>(['Yes', 'No'], (item, text) =>
        item.toLowerCase().includes(text.toLowerCase()),
      ),
    [],
  )
  const itemToString = useCallback((itemName: string) => itemName, [])
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <CustomSelect
      {...selectProps}
      focus$={focus$}
      onFocus={onFocus}
      isDisabled={isDisabled}
      onEnterDown={onEnterDown}
      itemToString={itemToString}
      multi={false}
      value={value}
      onChange={(value) => onChange(value === 'Yes')}
      placeholder={'Select one'}
      testId={testId}
    />
  )
}
